<template>
    <Teleport to="body">
        <Transition name="modal">
            <div
                v-if="isOpen"
                class="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/70 backdrop-blur-md"
                @click.self="$emit('close')"
            >
                <div
                    class="relative w-full max-w-2xl bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                    :style="modalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-1 relative z-10">
                        <div class="w-10 h-1 rounded-full bg-gray-300" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="$emit('close')"
                        class="absolute top-3 right-3 sm:top-6 sm:right-6 z-[60] w-11 h-11 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center transition-colors bg-gray-100 hover:bg-gray-200 sm:bg-transparent sm:hover:bg-gray-100 border-2 border-gray-300 sm:border-0 shadow-xl sm:shadow-none"
                        aria-label="Close modal"
                    >
                        <X class="w-6 h-6 sm:w-5 sm:h-5 text-gray-700 sm:text-gray-400 stroke-[3]" />
                    </button>

                    <!-- Scrollable Content -->
                    <div class="flex-1 overflow-y-auto">
                        <div class="flex flex-col lg:flex-row min-h-full">
                            <!-- Left Side - Context-Aware Content (Desktop Only) -->
                            <div class="hidden lg:block lg:w-2/5 bg-gradient-to-br from-zinc-900 to-black p-6 sm:p-8 text-white pt-14 sm:pt-8">
                                <img
                                    :src="STAR_ICON_URL"
                                    alt="Naughty America"
                                    class="h-7 sm:h-8 mb-4 sm:mb-6 opacity-90"
                                />

                                <!-- Auditions Context -->
                                <template v-if="isAuditionsContext">
                                    <h2 class="text-xl sm:text-2xl font-bold mb-2 leading-tight">
                                        Help {{ audition?.creator?.name }} Reach Her Goal
                                    </h2>
                                    <p class="text-white/70 text-sm mb-5 sm:mb-6 leading-relaxed">
                                        Your membership gives you stars to sponsor this audition
                                        plus full access to 14,000+ videos.
                                    </p>

                                    <!-- Performer Card -->
                                    <div class="mb-5 sm:mb-6">
                                        <div class="relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden mb-3 ring-2 ring-white/20">
                                            <img
                                                :src="audition?.creator?.avatar"
                                                :alt="audition?.creator?.name"
                                                class="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div class="space-y-1">
                                            <p class="text-sm font-semibold">{{ audition?.creator?.name }}</p>
                                            <p class="text-sm text-white/50 line-clamp-1">
                                                {{ audition?.title }}
                                            </p>
                                            <div class="flex items-center gap-2 text-sm text-white/60 mt-2">
                                                <span>{{ audition?.likes }} of {{ audition?.star_goal }} stars</span>
                                                <span v-if="starsNeeded > 0">•</span>
                                                <span v-if="starsNeeded > 0" class="text-emerald-400 font-semibold">
                                                    {{ starsNeeded }} needed
                                                </span>
                                            </div>
                                            <div class="relative h-1.5 bg-white/10 rounded-full overflow-hidden mt-2">
                                                <div
                                                    class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500"
                                                    :style="{ width: `${Math.min(percentage, 100)}%` }"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <!-- General Context -->
                                <template v-else>
                                    <h2 class="text-xl sm:text-2xl font-bold mb-2 leading-tight">
                                        Your Naughty America Access Starts Here
                                    </h2>
                                    <p class="text-white/70 text-sm mb-5 sm:mb-6 leading-relaxed">
                                        Get full access to 14,000+ exclusive videos plus stars to
                                        sponsor your favorite performers.
                                    </p>
                                </template>

                                <!-- Benefits Section -->
                                <div v-if="!isMockMode" class="space-y-2 pt-5 sm:pt-6 border-t border-white/10">
                                    <p class="text-xs uppercase tracking-wider text-white/40 font-semibold mb-3">
                                        Membership Includes
                                    </p>

                                    <div
                                        v-if="isAuditionsContext && selectedOptionData"
                                        class="flex items-start gap-2 mb-1"
                                    >
                                        <img
                                            :src="STAR_ICON_URL"
                                            alt="Star"
                                            class="w-4 h-4 flex-shrink-0 mt-0.5"
                                        />
                                        <span class="text-sm text-white font-semibold">
                                            {{ selectedOptionData.stars }} Stars
                                            {{ selectedOptionData.name === '3-Day Membership' ? 'to Start' : 'Every Month' }}
                                        </span>
                                    </div>

                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">14,000+ Exclusive Videos</span>
                                    </div>
                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">New Scenes Daily</span>
                                    </div>
                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">Watch in 4K & HD</span>
                                    </div>
                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">Stream or Download</span>
                                    </div>
                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">1,200+ VR Videos</span>
                                    </div>

                                    <!-- Stars benefit - only show for general context -->
                                    <div v-if="!isAuditionsContext" class="flex items-start gap-2">
                                        <img
                                            :src="STAR_ICON_URL"
                                            alt="Star"
                                            class="w-4 h-4 flex-shrink-0 mt-0.5"
                                        />
                                        <span class="text-sm text-white/90">Monthly Stars to Sponsor</span>
                                    </div>
                                </div>

                                <!-- Mock Mode: Star Benefits -->
                                <div v-else class="space-y-2 pt-5 sm:pt-6 border-t border-white/10">
                                    <p class="text-xs uppercase tracking-wider text-white/40 font-semibold mb-3">
                                        How Stars Work
                                    </p>

                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">Support your favorite performers</span>
                                    </div>
                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">Help decide which auditions get released</span>
                                    </div>
                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">Instant delivery</span>
                                    </div>
                                    <div class="flex items-start gap-2">
                                        <Check class="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                        <span class="text-sm text-white/90">Secure payment processing</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Right Side - Membership Selection -->
                            <div class="lg:w-3/5 p-6 sm:p-8 pb-8 sm:pb-8 pt-4 sm:pt-8">
                                <!-- Mobile Header Section -->
                                <div class="lg:hidden mb-5">
                                    <img
                                        :src="STAR_ICON_URL"
                                        alt="Naughty America"
                                        class="h-7 mb-3 opacity-90"
                                    />

                                    <div v-if="isAuditionsContext" class="mb-4">
                                        <div class="flex items-center gap-2.5 mb-2">
                                            <div class="relative w-16 h-16 rounded-lg overflow-hidden ring-2 ring-gray-200">
                                                <img
                                                    :src="audition?.creator?.avatar"
                                                    :alt="audition?.creator?.name"
                                                    class="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <p class="text-sm font-bold text-gray-900">{{ audition?.creator?.name }}</p>
                                                <p class="text-sm text-gray-600 line-clamp-1">{{ audition?.title }}</p>
                                            </div>
                                        </div>
                                        <div class="flex items-center gap-2 text-sm text-gray-600 mb-1.5">
                                            <span>{{ audition?.likes }} of {{ audition?.star_goal }} stars</span>
                                            <span v-if="starsNeeded > 0">•</span>
                                            <span v-if="starsNeeded > 0" class="text-emerald-600 font-semibold">{{ starsNeeded }} needed</span>
                                        </div>
                                        <div class="relative h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                class="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500"
                                                :style="{ width: `${Math.min(percentage, 100)}%` }"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <h3 class="text-lg sm:text-xl font-bold text-gray-900 mb-2 pr-12 sm:pr-0">
                                    {{ isAuditionsContext ? 'Join to Sponsor' : 'Join Naughty America' }}
                                </h3>
                                <p class="text-sm text-gray-600 mb-5 sm:mb-6 leading-relaxed">
                                    {{
                                        isAuditionsContext
                                            ? `Get stars to sponsor ${audition?.creator?.name}'s audition plus full access to our entire library.`
                                            : 'Start free or go all-in. Watch videos, buy stars, and upgrade anytime.'
                                    }}
                                </p>

                                <!-- Tier Selection -->
                                <div class="space-y-3 mb-5 sm:mb-6">
                                    <button
                                        v-for="option in joinOptions"
                                        :key="option.name"
                                        @click="selectedOption = option.name"
                                        :class="[
                                            'relative w-full p-3.5 sm:p-4 rounded-xl border-2 transition-all text-left active:scale-[0.98]',
                                            selectedOption === option.name
                                                ? 'border-blue-500 bg-blue-50 shadow-sm'
                                                : 'border-gray-200 hover:border-gray-300 bg-white'
                                        ]"
                                    >
                                        <div
                                            v-if="option.badge"
                                            class="absolute -top-2.5 right-3 sm:right-4 px-2.5 py-1 bg-emerald-600 text-white text-[10px] font-bold rounded-md shadow-sm"
                                        >
                                            {{ option.badge }}
                                        </div>
                                        <div class="flex items-start gap-2.5 sm:gap-3">
                                            <!-- Radio -->
                                            <div class="mt-0.5 sm:mt-1">
                                                <div
                                                    :class="[
                                                        'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                                                        selectedOption === option.name
                                                            ? 'border-blue-500 bg-blue-500'
                                                            : 'border-gray-300'
                                                    ]"
                                                >
                                                    <div
                                                        v-if="selectedOption === option.name"
                                                        class="w-2 h-2 bg-white rounded-full"
                                                    />
                                                </div>
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <div class="flex items-start justify-between mb-1 gap-3">
                                                    <div class="min-w-0">
                                                        <p class="font-bold text-gray-900 text-sm sm:text-base">
                                                            {{ option.name }}
                                                        </p>
                                                        <p class="text-[12px] text-gray-600 mt-0.5 leading-snug">
                                                            {{ option.description }}
                                                        </p>
                                                    </div>
                                                    <div class="text-right flex-shrink-0">
                                                        <p class="font-bold text-gray-900 text-base sm:text-lg whitespace-nowrap">
                                                            {{ option.displayPrice }}
                                                        </p>
                                                        <p class="text-xs text-gray-500">{{ option.period }}</p>
                                                        <p
                                                            v-if="option.subPrice"
                                                            class="text-xs text-emerald-600 font-semibold mt-0.5"
                                                        >
                                                            {{ option.subPrice }}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div class="flex items-center gap-1.5 mt-2">
                                                    <img
                                                        :src="STAR_ICON_URL"
                                                        alt="Star"
                                                        class="w-3.5 h-3.5"
                                                    />
                                                    <span class="text-xs sm:text-sm font-medium text-gray-700">
                                                        {{ option.stars_text }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                </div>

                                <!-- CTA Button -->
                                <button
                                    @click="handleJoin"
                                    class="w-full h-12 sm:h-auto sm:py-3.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-sm sm:text-base rounded-xl transition-all shadow-lg shadow-blue-600/25 active:scale-[0.98]"
                                >
                                    {{
                                        selectedOptionData?.type === 'trial'
                                            ? `Start Free Trial • ${selectedOptionData.stars} Stars`
                                            : `Get Full Access + ${selectedOptionData.stars} Stars/Month`
                                    }}
                                </button>

                                <!-- Trust Indicators -->
                                <div class="flex items-center justify-center gap-2 sm:gap-3 text-[11px] sm:text-xs text-gray-500 mt-3 sm:mt-4">
                                    <span class="flex items-center gap-1">
                                        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                                fill-rule="evenodd"
                                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                                clip-rule="evenodd"
                                            />
                                        </svg>
                                        Secure
                                    </span>
                                    <span>•</span>
                                    <span>7M+ members</span>
                                    <span>•</span>
                                    <span>Since 2001</span>
                                </div>

                                <!-- Mobile Benefits -->
                                <div v-if="!isMockMode" class="lg:hidden mt-[25px] mb-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                    <p class="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">
                                        Membership Includes
                                    </p>
                                    <div class="space-y-2">
                                        <div
                                            v-if="isAuditionsContext && selectedOptionData"
                                            class="flex items-start gap-2"
                                        >
                                            <img
                                                :src="STAR_ICON_URL"
                                                alt="Star"
                                                class="w-4 h-4 flex-shrink-0 mt-0.5"
                                            />
                                            <span class="text-sm text-gray-900 font-semibold">
                                                {{ selectedOptionData.stars }} Stars
                                                {{ selectedOptionData.name === '3-Day Membership' ? 'to Start' : 'Every Month' }}
                                            </span>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">14,000+ Exclusive Videos</span>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">New Scenes Daily</span>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">Watch in 4K & HD</span>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">Stream or Download</span>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">1,200+ VR Videos</span>
                                        </div>
                                        <div v-if="!isAuditionsContext" class="flex items-start gap-2">
                                            <img
                                                :src="STAR_ICON_URL"
                                                alt="Star"
                                                class="w-4 h-4 flex-shrink-0 mt-0.5"
                                            />
                                            <span class="text-sm text-gray-700">Monthly Stars to Sponsor</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Mobile Star Benefits (Mock Mode) -->
                                <div v-else class="lg:hidden mt-[25px] mb-5 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                    <p class="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">
                                        How Stars Work
                                    </p>
                                    <div class="space-y-2">
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">Support your favorite performers</span>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">Help decide which auditions get released</span>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">Instant delivery</span>
                                        </div>
                                        <div class="flex items-start gap-2">
                                            <Check class="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <span class="text-sm text-gray-700">Secure payment processing</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Footer -->
                                <div class="mt-5 sm:mt-6 pt-5 sm:pt-6 border-t border-gray-200">
                                    <p class="text-center text-sm text-gray-600">
                                        Already a member?
                                        <a :href="route('members2.auth.login', { dest: currentUrl })" class="text-blue-600 hover:text-blue-700 font-semibold active:underline">
                                            Sign in
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import { X, Check } from 'lucide-vue-next'
import {usePage} from "@inertiajs/vue3";
import {trackAuditionAnalyticsEvent} from "@/Helpers/auditions";
import {trackEvent} from "@/Helpers/analytics";

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    context: {
        type: String,
        default: 'general',
        validator: (value) => ['auditions', 'general'].includes(value)
    },
    audition: {
        type: Object,
        default: null
    },
    source: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['close'])

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const selectedOption = ref('3-Day Membership')
const windowHeight = ref(window.innerHeight)

// Mock mode detection
const isMockMode = computed(() => {
    if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search)
        const mockParam = params.get('mock')
        return mockParam === '1' || mockParam === '2'
    }
    return false
})

// Mock star packs data
const mockStarPacks = [
    { stars: 40, price: 9.99 },
    { stars: 80, price: 19.99 },
    { stars: 160, price: 39.99 },
    { stars: 1000, price: 249.99 },
    { stars: 4000, price: 999.99 },
]

const selectedStarPack = ref(mockStarPacks[1]) // Default to 80 stars pack

const modalStyle = computed(() => {
    // Desktop
    if (window.innerWidth >= 640) {
        return {
            maxHeight: '95vh'
        }
    }

    // Mobile: Use visual viewport or fallback
    const height = windowHeight.value || window.innerHeight
    return {
        maxHeight: `${Math.min(height * 0.95, height - 20)}px`
    }
})

const starsNeeded = computed(() => props.audition?.star_goal - props.audition?.likes)
const percentage = computed(() => (props.audition?.likes / props.audition?.star_goal) * 100)
const isAuditionsContext = computed(() => props.context === 'auditions')
const joinOptionsData = usePage().props.joinOptions

const joinOptions = computed(() => {
    const options = []
    if (joinOptionsData.button_trial) {
        const trialUrl = joinOptionsData.button_trial.url +
            (joinOptionsData.button_trial.url.includes('?') ? '&' : '?') +
            'vendor_option_id=2007234'
        options.push({
            name: joinOptionsData.button_trial.data.name,
            stars: joinOptionsData.button_trial.data.stars,
            stars_text: joinOptionsData.button_trial.data.stars_text,
            displayPrice: '$0.00',
            period: joinOptionsData.button_trial.data.period,
            description: 'Try 3 days free — cancel anytime',
            badge: 'FREE TRIAL',
            url: trialUrl,
            type: 'trial'
        })
    }
    // if (joinOptionsData.button_monthly) {
    //     options.push({
    //         name: joinOptionsData.button_monthly.data.name,
    //         stars: joinOptionsData.button_monthly.data.stars,
    //         stars_text: joinOptionsData.button_monthly.data.stars_text,
    //         displayPrice: `$${joinOptionsData.button_monthly.data.formatted_initial_amount}`,
    //         period: joinOptionsData.button_monthly.data.period,
    //         description: joinOptionsData.button_monthly.data.included_content,
    //         url: joinOptionsData.button_monthly.url,
    //         type: 'monthly'
    //     })
    // }
    // if (joinOptionsData.button_yearly) {
    //     options.push({
    //         name: joinOptionsData.button_yearly.data.name,
    //         stars: joinOptionsData.button_yearly.data.stars,
    //         stars_text: joinOptionsData.button_yearly.data.stars_text,
    //         displayPrice: `$${joinOptionsData.button_yearly.data.formatted_initial_amount}`,
    //         period: joinOptionsData.button_yearly.data.period,
    //         description: joinOptionsData.button_yearly.data.included_content,
    //         badge: joinOptionsData.button_yearly.data.saving ? `SAVE $${joinOptionsData.button_yearly.data.saving}` : null,
    //         subPrice: `Just $${joinOptionsData.button_yearly.data.formatted_monthly_amount}/month`,
    //         url: joinOptionsData.button_yearly.url,
    //         type: 'yearly'
    //     })
    // }

    return options;
})

const handleJoin = () => {
    const joinUrl = selectedOptionData.value.url +
        (selectedOptionData.value.url.includes('?') ?
            `&custom8=${props.source}` :
            `?custom8=${props.source}`)

    console.log('++ JoinOptions: From ' + props.source + ' Redirecting to', joinUrl);

    localStorage.setItem('blue_join_options_source', props.source);
    localStorage.setItem('blue_join_options_join_type', selectedOptionData.value.type);
    localStorage.setItem('blue_join_options_url', joinUrl);
    if (props.audition) {
        LTIAnalytics.setCookie("gold_upgrade_source", props.source);
        LTIAnalytics.setCookie("gold_upgrade_video_id", props.audition.id ?? '');
        trackAuditionAnalyticsEvent('Biller Join Button Clicked', props.audition, {
            store_in_local_storage: true,
            local_storage_key: 'blue_join_options_video_props',
            append_data: {
                source: props.source,
                join_type: selectedOptionData.value.type
            }
        });
    } else {
        trackEvent('Biller Join Button Clicked', {
            source: props.source,
            join_type: selectedOptionData.value.type
        });
    }

    window.location.href = joinUrl;
}

const selectedOptionData = computed(() =>
    joinOptions.value.find((t) => t.name === selectedOption.value)
)

const currentUrl = computed(() => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.pathname + window.location.search
    }
    return ''
})

// Touch handling for swipe-to-dismiss
const touchStartY = ref(0)
const touchEndY = ref(0)

const handleTouchStart = (e) => {
    // Only track touches on the pull handle area
    if (e.target.closest('.sm\\:hidden')) {
        touchStartY.value = e.touches[0].clientY
    }
}

const handleTouchMove = (e) => {
    if (touchStartY.value > 0) {
        touchEndY.value = e.touches[0].clientY
    }
}

const handleTouchEnd = () => {
    if (touchStartY.value > 0) {
        const swipeDistance = touchEndY.value - touchStartY.value

        // If swiped down more than 100px, close modal
        if (swipeDistance > 100) {
            emit('close')
        }
    }

    touchStartY.value = 0
    touchEndY.value = 0
}

onMounted(() => {
    const updateHeight = () => {
        windowHeight.value = window.visualViewport?.height || window.innerHeight
    }

    updateHeight()

    window.addEventListener('resize', updateHeight)
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', updateHeight)
    }

    return () => {
        window.removeEventListener('resize', updateHeight)
        if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', updateHeight)
        }
    }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
</style>
